import React, { useCallback, useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import Title from '../../component/Title'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import BottomButtonGroup from '../../component/BottomButtonGroup'
import Spiner from '../../component/Spiner'
import ListEmptyState from '../../component/ListEmptyState'
import { FORM_IMAGE_PREVIEW_FRAME_SX, FORM_PANEL_SX } from '../../component/formSurfaceStyles'
import { getLocationState } from '../../utils/listPageState'

const getBlockTypeLabel = (type) => {
    if (type === 1) {
        return '스팸 사용자(광고 및 게시글 도배)'
    }
    if (type === 2) {
        return '불괘감을 주는 이름, 프로필 사진'
    }
    if (type === 3) {
        return '올바르지 않은 채팅 사용(도배,비방,폭언,위협 등)'
    }
    if (type === 4) {
        return '부정 이용 사용자(gps 조작 등)'
    }

    return '기타'
}

const PANEL_SX = {
    ...FORM_PANEL_SX,
    height: '100%',
}

function FormSection({ title, description, children, sx = {} }) {
    return (
        <Paper variant="outlined" sx={{ ...PANEL_SX, ...sx }}>
            <Box sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {description}
                </Typography>
            </Box>
            {children}
        </Paper>
    )
}

function ProfileImagePreview({ imageSrc }) {
    return (
        <FormSection
            title="프로필 이미지"
            description="계정정지 대상 사용자의 앱 프로필 이미지를 확인합니다."
        >
            <Box
                sx={{
                    ...FORM_IMAGE_PREVIEW_FRAME_SX,
                    minHeight: 280,
                    height: 'auto',
                    maxHeight: 420,
                }}
            >
                {imageSrc ? (
                    <Box
                        component="img"
                        src={imageSrc}
                        alt="계정정지 대상 사용자 프로필 이미지"
                        sx={{
                            width: '100%',
                            maxHeight: 420,
                            objectFit: 'contain',
                            display: 'block',
                        }}
                    />
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        등록된 프로필 이미지가 없습니다.
                    </Typography>
                )}
            </Box>
        </FormSection>
    )
}

export default function AppBlock(props) {
    const history = useHistory()
    const location = useLocation()
    const locationState = getLocationState(location)
    const id = props.match.params.blockId
    const curPage = locationState.curPage || 1
    const searchText = locationState.searchText || ''
    const searchSelect = locationState.searchSelect || { searchType: '' }
    const listSearch = location.search || ''
    const [loading, setLoading] = useState(true)
    const [hasDetail, setHasDetail] = useState(false)
    const [params, setParams] = useState({})
    const [detailErrorMessage, setDetailErrorMessage] = useState('')
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('info')
    const [messageSnackbar, setMessageSnackbar] = useState('')

    const getData = useCallback(async () => {
        if (!id) {
            const errorMessage = '필수 파라미터가 없어 App 계정정지 상세를 불러올 수 없습니다.'
            setParams({})
            setHasDetail(false)
            setDetailErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
            setLoading(false)
            return
        }

        setLoading(true)
        setHasDetail(false)
        setDetailErrorMessage('')

        try {
            const { data } = await API.post('/api/v1/admin/appBlock/getDetail', { blockId: id })
            if (data.code === 200) {
                const detail = data.data || {}
                setParams(detail)
                setHasDetail(Boolean(detail?.blockId))
                setDetailErrorMessage(detail?.blockId ? '' : 'App 계정정지 상세 정보를 찾을 수 없습니다.')
            } else if (data.code === 403) {
                history.push('/login')
                return
            } else {
                const errorMessage = data.message || 'App 계정정지 상세를 불러오지 못했습니다.'
                setParams({})
                setDetailErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }
        } catch (_error) {
            const errorMessage = 'App 계정정지 상세를 불러오지 못했습니다.'
            setParams({})
            setDetailErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [history, id])

    useEffect(() => {
        getData()
    }, [getData])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    // 상세 조회 실패 시 목록 화면과 같은 오류 빈 상태 안내를 사용한다.
    const renderDetailErrorState = () => (
        <ListEmptyState
            variant="error"
            title="App 계정정지 상세를 불러오지 못했습니다."
            description={detailErrorMessage || '목록으로 돌아가 다시 확인하세요.'}
            action={(
                <BottomButtonGroup variant="emptyStateAction" path="/appBlocks" search={listSearch} visible={false} curPage={curPage} searchText={searchText} searchSelect={searchSelect} backLabel="목록으로"/>
            )}
            sx={{ mt: 2 }}
        />
    )

    if (loading) {
        return (
            <Grid item xs={12}>
                <Spiner/>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Grid>
        )
    }

    if (!hasDetail) {
        return (
            <Grid item xs={12}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Title>App 계정정지 상세</Title>
                    {renderDetailErrorState()}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            </Grid>
        )
    }

    const profileImage = params.user?.thumbnail
        ? `${process.env.REACT_APP_AWS_S3_URL}/${params.user.thumbnail}`
        : ''

    return (
        <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Title>App 계정정지 상세</Title>
                <Box>
                    <Grid container spacing={2} sx={{ marginTop: 2 }}>
                        <Grid item xs={12} md={4}>
                            <ProfileImagePreview imageSrc={profileImage}/>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <FormSection
                                title="계정 정보"
                                description="정지 대상 계정과 사용자 정보를 확인합니다."
                            >
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth>
                                            <TextField
                                                label="계정정지 ID"
                                                name="blockId"
                                                value={params.blockId || ''}
                                                size="small"
                                                InputProps={{ readOnly: true }}
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth>
                                            <TextField
                                                label="유저 ID"
                                                name="userId"
                                                value={params.userId || ''}
                                                size="small"
                                                InputProps={{ readOnly: true }}
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth>
                                            <TextField
                                                label="이름"
                                                name="username"
                                                value={params.user?.username || ''}
                                                size="small"
                                                InputProps={{ readOnly: true }}
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth>
                                            <TextField
                                                label="닉네임"
                                                name="nickName"
                                                value={params.user?.nickName || ''}
                                                size="small"
                                                InputProps={{ readOnly: true }}
                                            />
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </FormSection>
                        </Grid>
                        <Grid item xs={12}>
                            <FormSection
                                title="정지 정보"
                                description="계정정지 사유와 등록일을 확인합니다."
                            >
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={8}>
                                        <FormControl fullWidth>
                                            <TextField
                                                label="정지사유"
                                                name="type"
                                                value={getBlockTypeLabel(params.type)}
                                                size="small"
                                                InputProps={{ readOnly: true }}
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <FormControl fullWidth>
                                            <TextField
                                                label="등록일"
                                                name="regTime"
                                                value={params.regTime || ''}
                                                size="small"
                                                InputProps={{ readOnly: true }}
                                            />
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </FormSection>
                        </Grid>
                    </Grid>
                    <BottomButtonGroup
                        variant="formAction"
                        path="/appBlocks"
                        search={listSearch}
                        visible={false}
                        curPage={curPage}
                        searchText={searchText}
                        searchSelect={searchSelect}
                        actionTitle="계정정지 정보 확인"
                        actionDescription="확인 후 이전 App 계정정지 목록 위치로 돌아갑니다."
                    />
                </Box>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Paper>
        </Grid>
    )
}
